import { useMutation, useQueryClient } from "@tanstack/react-query";
import { extractStatus, normalizeWorkerResponse } from "./helpers";
import { showSuccessToast } from "../../utils/showToastAlert";

/**
 * @param {object} deps
 * @param {(url: string, options: any) => Promise<any>} deps.callApiWithWorker
 * @param {(key: string) => string} deps.t
 * @param {(err: any) => Promise<void> | void} [deps.onErrorAlert]
 * @returns mutation accepting { payload, connectorsCount }
 */
export function usePostParticipant({ callApiWithWorker, t, onErrorAlert }) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: ["participants", "create"],
    mutationFn: async ({ payload, connectorsCount }) => {
      const normalizedConnectorsCount = Number(connectorsCount);
      const url = Number.isFinite(normalizedConnectorsCount)
        ? `/registry/participants?${new URLSearchParams({
            connectorsCount: String(normalizedConnectorsCount),
          }).toString()}`
        : "/registry/participants";

      const res = await callApiWithWorker(url, {
        method: "POST",
        connector: "springConnector",
        body: payload,
      });

      const status = extractStatus(res);
      const data = normalizeWorkerResponse(res);

      if (status && status >= 400) {
        const e = new Error(`Create participant failed (${status})`);
        e.statusCode = status;
        e.response = data;
        throw e;
      }

      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["participants"] });
      showSuccessToast(
        t?.("participant.created", "Participant created successfully"),
        {
          title: t?.("dataModels.createdMessage", "Created successfully"),
        },
      );
    },
    onError: async (err) => {
      if (onErrorAlert) return onErrorAlert(err);
      console.error(t ? t("error") : "Error", err);
    },
  });
}
